/**
 * Regle de correspondance PAR ADRESSE (`secrets.uriMatch`), a la Bitwarden :
 * une entree peut dire, pour chacune de ses adresses, comment elle reconnait
 * une page. Sans regle, l'adresse suit la regle PAR DEFAUT DU COFFRE (reglage
 * de l'appareil, `DEFAULT_URI_MATCH_CHOICES`), « domaine » si rien n'est choisi.
 *
 *   - `domain`      : meme site (domaine enregistrable) + domaines equivalents,
 *                     sauf opt-out de l'entree -- ecrit explicitement, il tient
 *                     meme quand le defaut du coffre est plus strict ;
 *   - `host`        : meme hote ET meme port (`intranet.exemple.fr:8443`) --
 *                     deux applications d'un meme domaine restent distinctes ;
 *   - `starts-with` : la page commence par l'adresse, NORMALISEE d'abord
 *                     (`https://a.fr` devient `https://a.fr/`) : un prefixe
 *                     d'origine ne reconnait jamais `https://a.fr.pirate.example` ;
 *   - `exact`       : meme adresse, normalisee des deux cotes ;
 *   - `regex`       : expression reguliere sur l'adresse de la page (avance ;
 *                     une expression invalide ne reconnait rien) ;
 *   - `never`       : l'adresse est gardee mais ne reconnait aucune page.
 *
 * Le choix vit dans les secrets, chiffre, cle = l'adresse telle qu'enregistree :
 * il suit l'adresse si l'ordre change et disparait avec elle. Le serveur n'en
 * sait rien.
 */
import { isSameSite } from './domain.js';
import { isEquivalentSite } from './equivalent-domains.js';

export type UriMatchRule = 'domain' | 'host' | 'starts-with' | 'exact' | 'regex' | 'never';

export const URI_MATCH_RULES: readonly UriMatchRule[] = ['domain', 'host', 'starts-with', 'exact', 'regex', 'never'];

/**
 * Regles proposees comme defaut du coffre (« Default URI match detection » de Bitwarden). Chacune est AU PLUS
 * aussi large que le domaine : un reglage d'appareil trafique ne peut que restreindre ce qui est reconnu, jamais
 * l'elargir. Volontairement absentes : `regex` (chaque adresse lue comme une expression : `https://a.fr` y
 * reconnaitrait `https://aXfr.pirate.example`) et `never` (un coffre qui ne reconnait plus rien, sans le dire).
 */
export const DEFAULT_URI_MATCH_CHOICES: readonly UriMatchRule[] = ['domain', 'host', 'starts-with', 'exact'];

/** Defaut du coffre retenu ; hors des choix proposes (absent, trafique, `regex`, `never`), le domaine. */
export function defaultUriMatchRule(raw: unknown): UriMatchRule {
  return typeof raw === 'string' && (DEFAULT_URI_MATCH_CHOICES as readonly string[]).includes(raw) ? (raw as UriMatchRule) : 'domain';
}

/** Ce qu'une entree dit de ses sites. `equivalentDomains: false` = opt-out ; `uriMatch` = regle par adresse. */
export interface SiteMatchSecrets {
  readonly uris?: readonly string[];
  readonly equivalentDomains?: unknown;
  readonly uriMatch?: unknown;
}

export function isUriMatchRule(value: unknown): value is UriMatchRule {
  return typeof value === 'string' && (URI_MATCH_RULES as readonly string[]).includes(value);
}

/** Regle ecrite pour une adresse, `undefined` = defaut du coffre. Une valeur inconnue ou forgee vaut defaut du coffre. */
export function uriMatchRuleOf(secrets: SiteMatchSecrets, uri: string): UriMatchRule | undefined {
  const rules = secrets.uriMatch;
  if (typeof rules !== 'object' || rules === null || Array.isArray(rules)) return undefined;
  if (!Object.prototype.hasOwnProperty.call(rules, uri)) return undefined;
  const rule = (rules as Record<string, unknown>)[uri];
  return isUriMatchRule(rule) ? rule : undefined;
}

/**
 * Regles a enregistrer pour ces adresses : seules les regles choisies (`domain`
 * compris), une par adresse effectivement gardee ; `undefined` = defaut du coffre. `undefined` si aucune (la cle n'est pas
 * ecrite).
 */
export function normalizeUriMatch(
  uris: readonly string[],
  rules: ReadonlyMap<string, UriMatchRule | undefined>,
): Readonly<Record<string, UriMatchRule>> | undefined {
  const kept = new Map<string, UriMatchRule>();
  for (const uri of uris) {
    const rule = rules.get(uri);
    if (isUriMatchRule(rule) && !kept.has(uri)) kept.set(uri, rule);
  }
  // `fromEntries` cree des proprietes propres : une adresse « __proto__ » ne touche jamais au prototype.
  return kept.size > 0 ? Object.fromEntries(kept) : undefined;
}

/** URL web (http/https) normalisee, `https://` suppose sans schema ; `null` hors web ou illisible. */
function webUrl(uri: string): URL | null {
  const trimmed = uri.trim();
  if (trimmed === '') return null;
  const hasScheme = /^[a-z][a-z0-9+.-]*:/iu.test(trimmed) && !/^[^:/]+:\d+(?:[/?#]|$)/u.test(trimmed);
  try {
    const url = new URL(hasScheme ? trimmed : `https://${trimmed}`);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url : null;
  } catch {
    return null;
  }
}

/** L'adresse `uri` de l'entree reconnait-elle la page, selon sa regle ? */
export function uriMatchesPage(
  uri: string,
  rule: UriMatchRule | undefined,
  pageUri: string,
  equivalentDomains = true,
): boolean {
  switch (rule) {
    case undefined:
    case 'domain':
      return equivalentDomains ? isEquivalentSite(uri, pageUri) : isSameSite(uri, pageUri);
    case 'host': {
      const a = webUrl(uri);
      const b = webUrl(pageUri);
      return a !== null && b !== null && a.host === b.host;
    }
    case 'starts-with': {
      const prefix = webUrl(uri);
      const page = webUrl(pageUri);
      return prefix !== null && page !== null && page.href.startsWith(prefix.href);
    }
    case 'exact': {
      const a = webUrl(uri);
      const b = webUrl(pageUri);
      return a !== null && b !== null && a.href === b.href;
    }
    case 'regex':
      try {
        return new RegExp(uri, 'i').test(pageUri);
      } catch {
        return false;
      }
    case 'never':
      return false;
  }
}

/**
 * Adresses de l'entree qui reconnaissent la page (vide = l'entree ne correspond
 * pas). Chemin unique de reconnaissance : regle de l'adresse d'abord, sinon le
 * defaut du coffre (`defaultRule`, relu par `defaultUriMatchRule` : jamais
 * `regex` ni `never`), sinon meme site + equivalents, sauf opt-out de l'entree.
 */
export function entryUrisMatchingSite(secrets: SiteMatchSecrets, pageUri: string, defaultRule?: unknown): string[] {
  const equivalents = secrets.equivalentDomains !== false;
  const fallback = defaultUriMatchRule(defaultRule);
  return (secrets.uris ?? []).filter(
    (uri) => typeof uri === 'string' && uriMatchesPage(uri, uriMatchRuleOf(secrets, uri) ?? fallback, pageUri, equivalents),
  );
}

/** L'entree correspond-elle a la page ? Au moins une de ses adresses la reconnait. */
export function entryMatchesSite(secrets: SiteMatchSecrets, pageUri: string, defaultRule?: unknown): boolean {
  return entryUrisMatchingSite(secrets, pageUri, defaultRule).length > 0;
}
